/**
 * PhoneBrowser.tsx
 *
 * TLAM web browser app — REAL Chromium running on the Pi (inside the modem
 * network namespace), streamed to this <video> over WebRTC. Replaces the old
 * iframe + HTML-rewrite proxy, which couldn't render modern sites (Google
 * search came back blank — no cookie bridging, JS-built URLs the rewriter
 * missed). Now it's a real browser: real cookies, real JS, real SPAs, and every
 * page byte traverses the cellular link.
 *
 * The streamed video is the FULL Chromium window — its own tab strip, URL bar,
 * back/forward/reload — so there is NO custom control bar here. The user drives
 * the real Chrome chrome directly: taps/scroll/keys are forwarded over a WebRTC
 * data channel and injected on the Pi via xdotool (global, so they reach the
 * chrome AND the page), at 1:1 pixel coords (window is at 0,0 sized to the
 * viewport). The <video> fills the whole phone screen.
 *
 * Lifetime: leaving this app (unmount) only DETACHES the video — Chromium stays
 * warm on the Pi (the modem allocation still holds the data session), so
 * re-opening re-attaches instantly to the same page. The browser is killed only
 * when the modem allocation is released (End session / modal close / TTL), via
 * the edge's data_plane_teardown. So we deliberately do NOT POST /browser/stop
 * on unmount.
 *
 * See plans/tlam-real-browser-chromium.md +
 * docs/sessions/SESSION_2026_05_31_TLAM_REAL_BROWSER_CHROMIUM.md.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Globe } from 'lucide-react';
import phoneWebSocketService from '@/services/phoneWebSocketService';
import { auth } from '@/config/firebase';
import { API_BASE_URL } from '@/config/env';

// The video is the full Chromium window sized to the phone viewport and mapped
// 1:1 to X coords (window at 0,0). No chrome offset — input targets whatever the
// user taps, chrome included.
const VIEW_W = 412;
const VIEW_H = 915;

// Special keys we forward by name (xdotool keysyms). Anything else printable
// goes as text. Kept in sync with the edge allowlist (_ALLOWED_KEYNAMES).
const KEYNAME_MAP: Record<string, string> = {
  Enter: 'Return',
  Backspace: 'BackSpace',
  Tab: 'Tab',
  Escape: 'Escape',
  Delete: 'Delete',
  ArrowUp: 'Up',
  ArrowDown: 'Down',
  ArrowLeft: 'Left',
  ArrowRight: 'Right',
  Home: 'Home',
  End: 'End',
  PageUp: 'Page_Up',
  PageDown: 'Page_Down',
};

export interface PhoneBrowserProps {
  onBack: () => void;
  disabled?: boolean;
}

type Status = 'idle' | 'connecting' | 'live' | 'failed';

export const PhoneBrowser: React.FC<PhoneBrowserProps> = ({ disabled = false }) => {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const startedRef = useRef(false);

  // ---- input plumbing --------------------------------------------------

  /** Send an input event to the Pi: prefer the WebRTC data channel (low
   *  latency), fall back to the HTTP relay if the channel isn't open. */
  const sendInput = useCallback((ev: Record<string, unknown>) => {
    const dc = dcRef.current;
    if (dc && dc.readyState === 'open') {
      try { dc.send(JSON.stringify(ev)); return; } catch { /* fall through */ }
    }
    const sessionId = phoneWebSocketService.getSessionId();
    const allocationId = phoneWebSocketService.getAllocationId();
    if (!sessionId || !allocationId) return;
    auth.currentUser?.getIdToken().then((token) => {
      fetch(`${API_BASE_URL}/tlam/sessions/${encodeURIComponent(sessionId)}/browser/input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ allocationId, event: ev }),
      }).catch(() => { /* best-effort */ });
    });
  }, []);

  // Translate a DOM pointer/wheel event on the <video> into capture-window X
  // coords (the Pi window is 412×915 at 0,0). With objectFit:'contain' the
  // rendered image is letterboxed inside the element rect, so we must map
  // against the DISPLAYED IMAGE rect (rect minus letterbox bars), not the raw
  // element rect — otherwise every tap is offset by the bar size.
  const toWindow = (e: React.PointerEvent | React.WheelEvent) => {
    const el = videoRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const videoAR = VIEW_W / VIEW_H;               // 0.450 (capture aspect)
    const elAR = rect.width / rect.height;
    let dispW = rect.width, dispH = rect.height, offX = 0, offY = 0;
    if (elAR > videoAR) {
      // element wider than video → pillarbox (bars left/right)
      dispW = rect.height * videoAR;
      offX = (rect.width - dispW) / 2;
    } else {
      // element taller than video → letterbox (bars top/bottom)
      dispH = rect.width / videoAR;
      offY = (rect.height - dispH) / 2;
    }
    const ix = e.clientX - rect.left - offX;
    const iy = e.clientY - rect.top - offY;
    if (ix < 0 || iy < 0 || ix > dispW || iy > dispH) return null; // tap in a bar
    return {
      x: Math.round((ix / dispW) * VIEW_W),
      y: Math.round((iy / dispH) * VIEW_H),
    };
  };

  // ---- session lifecycle ----------------------------------------------

  const startSession = useCallback(async () => {
    if (startedRef.current || disabled) return;
    const sessionId = phoneWebSocketService.getSessionId();
    const allocationId = phoneWebSocketService.getAllocationId();
    if (!sessionId || !allocationId) {
      setError('No active session/allocation');
      setStatus('failed');
      return;
    }
    startedRef.current = true;
    setStatus('connecting');
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      // ICE creds (reuse the voice audio/credentials endpoint — same TURN).
      const credsResp = await fetch(
        `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(sessionId)}/audio/credentials` +
          `?callId=browser&allocationId=${encodeURIComponent(allocationId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const creds = credsResp.ok ? await credsResp.json() : { iceServers: [] };
      const iceServers: RTCIceServer[] = (creds.iceServers || []).map((s: any) => ({
        urls: s.urls,
        ...(s.username ? { username: s.username } : {}),
        ...(s.credential ? { credential: s.credential } : {}),
      }));

      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;
      const transceiver = pc.addTransceiver('video', { direction: 'recvonly' });
      // Offer VP8 first. The Pi (aiortc) answers with the offer's first codec it
      // supports; Safari lists H.264 first, and the Pi's H.264 path can't open
      // an encoder for the 412×915 capture (odd height; no h264_omx) → the
      // stream never sends a frame and the phone screen stays black. Chrome
      // already offers VP8 first. Older browsers without setCodecPreferences
      // keep their default order.
      try {
        const codecs = RTCRtpReceiver.getCapabilities?.('video')?.codecs ?? [];
        const vp8 = codecs.filter((c) => c.mimeType.toLowerCase() === 'video/vp8');
        if (vp8.length && typeof transceiver.setCodecPreferences === 'function') {
          transceiver.setCodecPreferences([
            ...vp8,
            ...codecs.filter((c) => c.mimeType.toLowerCase() !== 'video/vp8'),
          ]);
        }
      } catch (e) {
        console.warn('[PhoneBrowser] could not prefer VP8:', e);
      }
      const dc = pc.createDataChannel('input');
      dcRef.current = dc;

      pc.ontrack = (e) => {
        if (videoRef.current && e.streams[0]) {
          videoRef.current.srcObject = e.streams[0];
          videoRef.current.play().catch(() => { /* autoplay; muted handles it */ });
        }
      };
      pc.oniceconnectionstatechange = () => {
        const st = pc.iceConnectionState;
        if (st === 'connected' || st === 'completed') setStatus('live');
        else if (st === 'failed') { setStatus('failed'); setError('Connection lost'); }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      // Wait for ICE gathering (vanilla — answer carries all candidates).
      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === 'complete') return resolve();
        const check = () => {
          if (pc.iceGatheringState === 'complete') {
            pc.removeEventListener('icegatheringstatechange', check);
            resolve();
          }
        };
        pc.addEventListener('icegatheringstatechange', check);
        setTimeout(resolve, 4000);
      });

      const apn = phoneWebSocketService.getApn() || undefined;
      const resp = await fetch(
        `${API_BASE_URL}/tlam/sessions/${encodeURIComponent(sessionId)}/browser/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            allocationId,
            sdp: pc.localDescription?.sdp,
            iceServers: creds.iceServers || [],
            apn,
            url: 'https://www.google.com',
          }),
        },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.reason || body?.error || `browser/start HTTP ${resp.status}`);
      }
      const answer = await resp.json();
      await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
    } catch (err: any) {
      console.error('[PhoneBrowser] start failed:', err);
      setError(err?.message || 'Failed to start browser');
      setStatus('failed');
      startedRef.current = false;
    }
  }, [disabled]);

  // Auto-start on mount. On unmount, DETACH only (drop the local peer) — do NOT
  // POST /browser/stop: Chromium stays warm on the Pi for an instant re-open;
  // the real kill happens on modem-allocation release (End session / TTL).
  useEffect(() => {
    startSession();
    return () => {
      try { dcRef.current?.close(); } catch { /* ignore */ }
      try { pcRef.current?.close(); } catch { /* ignore */ }
      pcRef.current = null;
      dcRef.current = null;
      startedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- input handlers --------------------------------------------------

  const onPointerDown = (e: React.PointerEvent) => {
    if (status !== 'live') return;
    videoRef.current?.focus();
    const p = toWindow(e);
    if (p) sendInput({ t: 'tap', x: p.x, y: p.y });
  };

  const onWheel = (e: React.WheelEvent) => {
    if (status !== 'live') return;
    const p = toWindow(e);
    if (p) sendInput({ t: 'scroll', x: p.x, y: p.y, dy: -e.deltaY });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (status !== 'live') return;
    // The focused video IS the browser keyboard — swallow EVERY key so it goes
    // to Chromium via xdotool and never leaks to the host app's global keyboard
    // shortcuts (typing a URL like "g…" would otherwise trigger app nav/command
    // palette and tear the TLAM session down). stopPropagation also halts the
    // native event at the React root so window-level listeners don't fire.
    e.preventDefault();
    e.stopPropagation();
    const mapped = KEYNAME_MAP[e.key];
    if (mapped) {
      sendInput({ t: 'keyname', name: mapped });
    } else if (e.key.length === 1) {
      // Ctrl/Cmd combos → named (clipboard etc.); plain char → text.
      if (e.ctrlKey || e.metaKey) {
        const combo = `ctrl+${e.key.toLowerCase()}`;
        sendInput({ t: 'keyname', name: combo });
      } else {
        sendInput({ t: 'key', text: e.key });
      }
    }
  };

  // Also swallow keyup/keypress while live so no residual key event reaches the
  // host app's shortcut handlers (some listen on keyup/keypress, not keydown).
  const onKeySwallow = (e: React.KeyboardEvent) => {
    if (status !== 'live') return;
    e.preventDefault();
    e.stopPropagation();
  };

  // The React onKeyDown above is NOT sufficient on its own: the TLAM modal is a
  // Radix Dialog rendered in a PORTAL at document.body — outside React's root
  // container — so a native keystroke bubbles video → body → window and the
  // app's window-level `useGlobalKeyboardShortcuts` listener fires before
  // React's synthetic stopPropagation can intercept the native path (e.g.
  // typing "g…" in a URL triggered the "g then s → /settings" nav shortcut and
  // tore the session down). Fix: a CAPTURE-phase window listener that runs
  // before ANY bubble-phase window handler, active only while the browser is
  // live AND the video is focused. It forwards the key to Chromium (xdotool)
  // and stopImmediatePropagation()s so app shortcuts never see it.
  useEffect(() => {
    if (status !== 'live') return;
    const ownsKeys = () => document.activeElement === videoRef.current;
    const swallow = (e: KeyboardEvent) => {
      if (!ownsKeys()) return; // not focused on the browser — let the app have its keys
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.type !== 'keydown') return;
      const mapped = KEYNAME_MAP[e.key];
      if (mapped) {
        sendInput({ t: 'keyname', name: mapped });
      } else if (e.key.length === 1) {
        if (e.ctrlKey || e.metaKey) {
          sendInput({ t: 'keyname', name: `ctrl+${e.key.toLowerCase()}` });
        } else {
          sendInput({ t: 'key', text: e.key });
        }
      }
    };
    window.addEventListener('keydown', swallow, true);
    window.addEventListener('keyup', swallow, true);
    window.addEventListener('keypress', swallow, true);
    return () => {
      window.removeEventListener('keydown', swallow, true);
      window.removeEventListener('keyup', swallow, true);
      window.removeEventListener('keypress', swallow, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // ---- render ----------------------------------------------------------
  // The video IS the whole UI — no control bar; the streamed Chrome has its own.

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 overflow-hidden bg-background relative">
        <video
          ref={videoRef}
          data-testid="tlam-browser-video"
          autoPlay
          playsInline
          muted
          tabIndex={0}
          onPointerDown={onPointerDown}
          onWheel={onWheel}
          onKeyDown={onKeyDown}
          onKeyUp={onKeySwallow}
          onKeyPress={onKeySwallow}
          className="w-full h-full bg-black outline-none"
          // 'contain' preserves the 412×915 capture aspect ratio — no stretch.
          // The phone frame (~390×866) is close to 412:915 (0.45) so any
          // letterbox is minimal; black bars beat a distorted page. Input
          // mapping in toWindow() already uses the element's real rect, but with
          // 'contain' the rendered image may be inset within that rect — see the
          // letterbox-aware mapping in toWindow().
          style={{ touchAction: 'none', objectFit: 'contain' }}
        />

        {status === 'connecting' && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
            <div className="text-center text-muted-foreground">
              <Globe className="w-12 h-12 mx-auto mb-2 animate-pulse" />
              <p className="text-sm">Starting browser…</p>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="absolute inset-0 flex items-center justify-center p-4 bg-background">
            <div className="text-center w-full">
              <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-status-error-bg flex items-center justify-center">
                <Globe className="w-6 h-6 text-status-error" />
              </div>
              <p className="text-status-error font-medium mb-1">Browser unavailable</p>
              <p className="text-xs text-muted-foreground break-all mb-3">
                {error || 'Could not start the cellular browser.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
